import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Card, CardContent, CircularProgress, Alert, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Avatar } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { reportsAPI } from '../../services/api';

interface AgentProductivityWorkloadProps {
  startDate: string;
  endDate: string;
}

const AgentProductivityWorkload: React.FC<AgentProductivityWorkloadProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    fetchAgentProductivity();
  }, [startDate, endDate]);

  const fetchAgentProductivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await reportsAPI.getAgentReport({
        start_date: startDate,
        end_date: endDate
      });

      // Transform backend data to component format
      const agentList = (response.agents || []).map((agent: any) => {
        // Calculate productivity score based on metrics
        const resolutionRate = agent.conversations_handled > 0
          ? (agent.resolution_count / agent.conversations_handled) * 100
          : 0;

        const csatPercentage = (agent.csat_score / 5.0) * 100;

        // Productivity score: 40% conversations, 30% resolution rate, 30% CSAT
        const productivity_score = Math.round(
          (Math.min(agent.conversations_handled, 100) * 0.4) +
          (resolutionRate * 0.3) +
          (csatPercentage * 0.3)
        );

        // Workload estimation (normalized to 0-100 scale)
        const maxConversations = 150; // Assumed max for normalization
        const workload = Math.min((agent.conversations_handled / maxConversations) * 100, 100);

        // Trend based on resolution rate
        const trend = resolutionRate > 70 ? 'up' : 'down';

        return {
          id: agent.agent_id,
          name: agent.agent_name,
          conversations: agent.conversations_handled,
          messages: agent.messages_sent,
          avg_handle_time: agent.avg_response_time,
          productivity_score,
          workload: Math.round(workload),
          trend
        };
      });

      setData({
        agents: agentList,
        avg_productivity: agentList.length > 0
          ? agentList.reduce((sum: number, a: any) => sum + a.productivity_score, 0) / agentList.length
          : 0,
        total_conversations: agentList.reduce((sum: number, a: any) => sum + a.conversations, 0),
        workload_distribution: agentList.map((a: any) => ({ name: a.name, workload: a.workload }))
      });
    } catch (err: any) {
      console.error('Failed to fetch agent productivity:', err);
      setError(err.message || 'Failed to load agent productivity data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || !data.agents || data.agents.length === 0) return <Alert severity="info">No productivity data available for the selected period</Alert>;

  const workloadChartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: true, formatter: (val) => `${val}%` },
    xaxis: { categories: data.workload_distribution.map((d: any) => d.name), max: 100 },
    colors: ['#1f93ff']
  };

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Avg Productivity Score</Typography>
            <Typography variant="h4" color="primary">{data.avg_productivity.toFixed(1)}</Typography>
            <Typography variant="caption">Across all agents</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Total Conversations</Typography>
            <Typography variant="h4" color="primary">{data.total_conversations}</Typography>
            <Typography variant="caption">Last 30 days</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Active Agents</Typography>
            <Typography variant="h4" color="primary">{data.agents.length}</Typography>
            <Typography variant="caption">With activity</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Workload Distribution</Typography>
        <Chart
          options={workloadChartOptions}
          series={[{ name: 'Workload', data: data.workload_distribution.map((d: any) => d.workload) }]}
          type="bar"
          height={250}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Agent Performance Details</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Conversations</TableCell>
                <TableCell align="right">Messages</TableCell>
                <TableCell align="right">Avg Handle Time</TableCell>
                <TableCell align="right">Productivity Score</TableCell>
                <TableCell align="center">Workload</TableCell>
                <TableCell align="center">Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.agents.map((agent: any) => (
                <TableRow key={agent.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32 }}>{agent.name[0]}</Avatar>
                      {agent.name}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{agent.conversations}</TableCell>
                  <TableCell align="right">{agent.messages}</TableCell>
                  <TableCell align="right">{agent.avg_handle_time} min</TableCell>
                  <TableCell align="right">
                    <Chip label={agent.productivity_score} size="small" color={agent.productivity_score >= 85 ? 'success' : 'warning'} />
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="body2">{agent.workload}%</Typography>
                      <LinearProgress variant="determinate" value={agent.workload} sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {agent.trend === 'up' ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2">💡 Productivity Insights</Typography>
        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
          <li><Typography variant="caption">Productivity score considers: conversations handled, resolution rate, CSAT, and handle time</Typography></li>
          <li><Typography variant="caption">Optimal workload: 70-85% for sustained performance</Typography></li>
          <li><Typography variant="caption">Agents with {'>'}85% workload may need support or task redistribution</Typography></li>
        </ul>
      </Alert>
    </Box>
  );
};

export default AgentProductivityWorkload;
